import '../styles/CallCenterReport.scss';
import { Button, Form } from 'react-bootstrap';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import Multiselect from 'multiselect-react-dropdown';
import UniDatePickerRange from '../components/UniDatePickerRange/UniDatePickerRange';
import filterIcon from '../assets/icons/filter.svg';
import graph from '../assets/icons/graph.svg';
import documentExcel from '../assets/icons/document-excel.svg';
import { getAllCustomer} from '../services/customerService';
import UniModal from '../components/UniModal/UniModal';
import { faXmarkCircle } from '@fortawesome/free-solid-svg-icons';
import { UniWidgetColumnTwoOptions } from '../components/UniWidgetColumnReportTwo/UniWidgetColumnReportTwo';
import { GetFileReport, GetReportData, getSectors, getTicketLevels, ITicketTotalStatusDto, RequestReportData, ResponseReportData, TicketStatusEnum } from '../services/callCenterService';
import UniInput from '../components/UniInput/UniInput';
import UniWidgetDobleColumnReport, { UniWidgetDobleColumnReportOptions } from '../components/UniWidgetDobleColumnReport/UniWidgetDobleColumnReport';
import UniWidgetCardGraph from '../components/UniWidgetCardGraph/UniWidgetCardGraph';

type MultiselectOptions = {
    id: string
    name: string,
};

const CallCenterReport = () =>{
    const { i18n, t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(true);
    const [periodValue, setPeriodValue] = useState<string | null>('');
    const [startDate, setStartDate] = useState<Date | any>(undefined);
    const [endDate, setEndDate] = useState<Date | any>(undefined);
    const [showRangeDates, setShowRangeDates] = useState(false);
    const [currentTab, setCurrentTab] = useState(1);
    const [optionsCustomers, setOptionsCustomers] = useState<MultiselectOptions[]>([]);
    const [optionsSectors, setOptionsSectors] = useState<MultiselectOptions[]>([]);
    const [optionsTicketLevels, setOptionsTicketLevels] = useState<MultiselectOptions[]>([]);
    const [filterOptions, setFilterOptions] = useState<UniWidgetColumnTwoOptions[]>([{ value: new Date().getFullYear(), description: new Date().getFullYear() + '' }]);    
    const [customerSelectedValues, setCustomerSelectedValues] = useState<any>([]);
    const [sectorSelectedValues, setSectorSelectedValues] = useState<any>([]);
    const [ticketLevelSelectedValues, setTicketLevelSelectedValues] = useState<any>([]);
    const [reportData, setReportData] = useState<ResponseReportData>();
    const [reportDataResolved, setReportDataResolved] = useState<ITicketTotalStatusDto | undefined>();
    const [reportDataFinished, setReportDataFinished] = useState<ITicketTotalStatusDto | undefined>();
    const [reportDataPending, setReportDataPending] = useState<ITicketTotalStatusDto | undefined>();
    const [filteredSectorData, setFilteredSectorData] = useState<UniWidgetDobleColumnReportOptions[][] | undefined>();
    const [filteredTypeData, setFilteredTypeData] = useState<UniWidgetDobleColumnReportOptions[][] | undefined>();
    const [errorAccessAs, setErrorAccessAs] = useState('');
    const [showModalComment, setShowModalComment] = useState(false);
    const [countryFilter, setCountryFilter] = useState('');
    const [groupFilter, setGroupFilter] = useState('');

    useEffect(() => {
        getCustomersToFilter();
        getSectorsToFilter();
        getTicketLevelsToFilter();
    },[]);

    useEffect(() => {
        getSectorsToFilter();
        getTicketLevelsToFilter();
        
        const dataResolved = reportData?.totalStatus?.filter(t => t.status === TicketStatusEnum.Served)?.[0];
        const dataFinished = reportData?.totalStatus?.filter(t => t.status === TicketStatusEnum.Finished)?.[0];
        const dataOpen = reportData?.totalStatus?.filter(t => t.status === TicketStatusEnum.Pending)?.[0];
        
        var sectorFinisheds: UniWidgetDobleColumnReportOptions[][] | undefined = dataFinished?.totalSectors?.filter(s => s.totalTickets > 0)?.map(s => (
            [
                {
                    description: i18n.language == `pt` ? s.descriptionPT : i18n.language == `es` ? s.descriptionES : s.descriptionEN,
                    value: s.totalNotIsLate,
                    color: '#1BC76D',
                },
                {
                    description: i18n.language == `pt` ? s.descriptionPT : i18n.language == `es` ? s.descriptionES : s.descriptionEN,
                    value: s.totalIsLate,
                    color: '#FF6636',
                }
            ]
        ));

        var levelFinisheds: UniWidgetDobleColumnReportOptions[][] | undefined = dataFinished?.totalLevels?.filter(s => s.totalTickets > 0)?.map(s => (
            [
                {
                    description: i18n.language == `pt` ? s.descriptionPT : i18n.language == `es` ? s.descriptionES : s.descriptionEN,
                    value: s.totalNotIsLate,
                    color: '#1BC76D',
                },
                {
                    description: i18n.language == `pt` ? s.descriptionPT : i18n.language == `es` ? s.descriptionES : s.descriptionEN,
                    value: s.totalIsLate,
                    color: '#FF6636',
                }
            ]
        ));

        setReportDataResolved(dataResolved);
        setReportDataFinished(dataFinished);
        setReportDataPending(dataOpen);
        setFilteredSectorData(sectorFinisheds);
        setFilteredTypeData(levelFinisheds);
    }, [i18n.language, reportData]);

    const allTicketOpenedCount = useMemo(() => {
        return ((reportDataPending?.totalTickets ?? 0) + (reportDataResolved?.totalTickets ?? 0))
      }, [reportDataPending, reportDataResolved]);

    const onChangeCustomerMulti = (e: MultiselectOptions[]) => {
        setCustomerSelectedValues(e.map(x => x.id));
    }    

    const onChangeSectorMulti = (e: MultiselectOptions[]) => {
        setSectorSelectedValues(e.map(x => x.id));
    }  

    const onChangeTicketLevelMulti = (e: MultiselectOptions[]) => {
        setTicketLevelSelectedValues(e.map(x => x.id));
    }

    const getCustomersToFilter = () => {
        setLoading(true);
        getAllCustomer().then((customers)=> {
            setOptionsCustomers(customers.data.map(x => {
                return {
                    id: x.id,
                    name: x.issuerName
                }
            }) ?? []);
        })
        .catch(() => setOptionsCustomers([]))
        .finally(() => setLoading(false));
    }

    const getSectorsToFilter = () => {
        setLoading(true);
        getSectors().then((sectors)=> {
            setOptionsSectors(sectors.data.map(x => {
                return {
                    id: x.id,
                    name: i18n.language === 'pt' ? x.namePT : i18n.language === 'es' ? x.nameES : x.nameEN
                }
            }) ?? []);
        })
        .catch(() => setOptionsSectors([]))
        .finally(() => setLoading(false));
    }

    const getTicketLevelsToFilter = () => {
        setLoading(true);
        getTicketLevels().then((ticketLevels)=> {
            setOptionsTicketLevels(ticketLevels.data.map(x => {
                return {
                    id: x.id,
                    name: i18n.language === 'pt' ? x.descriptionPT : i18n.language === 'es' ? x.descriptionES : x.descriptionEN
                }
            }) ?? []);
        })
        .catch(() => setOptionsTicketLevels([]))
        .finally(() => setLoading(false));
    }

    const onChangeDates = (dates: any) => {
        const [start, end] = dates;
        setPeriodValue(dateRange(start,end));
        setShowRangeDates(false);
        setStartDate(start);
        setEndDate(end);
        return;
    };

    const dateRange = (start: any, end: any) => {
        return moment(start).format('DD/MM/yyyy') + ' - ' + moment(end).format('DD/MM/yyyy');
    }

    const isValidDate = () => {
        if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
            setErrorAccessAs(t('periodAlert.periodRequired'));
            return false;
        }

        if (isNaN(new Date(startDate).getTime()) || isNaN(new Date(endDate).getTime())) {
            setErrorAccessAs(t('periodAlert.invalidDate'));
            return false;
        }

        return true;
    }

    const mountPayload = () => {
        const request: RequestReportData = {
            customers: customerSelectedValues,
            sectors: sectorSelectedValues,
            levels: ticketLevelSelectedValues,
            createdDateInitial: moment(startDate).format('yyyy-MM-DD'),
            createdDateFinal: moment(endDate).format('yyyy-MM-DD'),
            isPVC: groupFilter === `true` ? true : groupFilter === `false` ? false : null,
            country: countryFilter
        };

        return request;
    }

    const getReport = () => {
        if (!isValidDate()) {
            return;
        }

        setLoading(true);
        GetReportData(mountPayload())
            .then(m => {
                if (m.success) {
                    setReportData(m.data);                    
                }
            })
            .catch(e => console.log(e))
            .finally(() => setLoading(false));
    }

    const downloadFile = async () => {
        if (!isValidDate()) {
            return;
        }
        setLoading(true);
        await GetFileReport(mountPayload(), i18n.language.toUpperCase()).finally(() => setLoading(false));
    }
    
    return (        
        <div className='call-center-report row'>
            <div className='filter-wrapper col-lg-4'>
                <div className='header'>
                    <div className='menu-icon'>
                        <img src={filterIcon} alt='icone de filtro' />
                    </div>
                    <div className='header'>
                        {t('filterData')}
                    </div>
                </div>
                <div className='body'>
                    <div className='filter'>
                        <Form.Label htmlFor='customers'>{t('customers')}</Form.Label>
                        <Multiselect
                            options={optionsCustomers}
                            onSelect={(e: any) => onChangeCustomerMulti(e)}
                            onRemove={(e: any) => onChangeCustomerMulti(e)}
                            displayValue='name'
                            placeholder={t('select')}
                            disable={loading}
                            hidePlaceholder
                            showCheckbox
                            showArrow
                        />
                    </div>

                    <div className='filter'>
                        <Form.Label htmlFor='sector'>{t('callCenter.sector')}</Form.Label>
                        <Multiselect
                            options={optionsSectors}
                            onSelect={(e: any) => onChangeSectorMulti(e)}
                            onRemove={(e: any) => onChangeSectorMulti(e)}
                            displayValue='name'
                            placeholder={t('select')}
                            disable={loading}
                            hidePlaceholder
                            showCheckbox
                            showArrow
                        />
                    </div>

                    <div className='filter'>
                        <Form.Label htmlFor='sector'>{t('callCenter.labelSubject')}</Form.Label>
                        <Multiselect
                            options={optionsTicketLevels}
                            onSelect={(e: any) => onChangeTicketLevelMulti(e)}
                            onRemove={(e: any) => onChangeTicketLevelMulti(e)}
                            displayValue='name'
                            placeholder={t('select')}
                            disable={loading}
                            hidePlaceholder
                            showCheckbox
                            showArrow
                        />
                    </div>                    

                    <div className='filter'>
                        <UniInput
                            id='Pais'
                            label={t('user.labelCountry')}
                            isSelect
                            defaultValue={''}
                            onChange={e => setCountryFilter(e.target.value)}
                        >
                            <option value={''}>{t('homeScreen.selectAll')}</option>
                            {<option value={'BR'}>{t('user.brazil')}</option>}
                            {<option value={'AR'}>Argentina</option>}
                        </UniInput>
                    </div>

                    <div className='filter'>
                        <UniInput
                            id='tpProduct'
                            label={t('homeScreen.groupProducts')}
                            isSelect
                            defaultValue={''}
                            onChange={e => setGroupFilter(e.target.value)}
                        >
                            <option value={''} selected>{t('homeScreen.selectAll')}</option>
                            <option value={'true'}>PVC</option>
                            <option value={'false'}>{t('homeScreen.chemicals')}</option>
                        </UniInput>
                    </div>

                    <div className='filter'>
                        <UniDatePickerRange
                            loading={loading}
                            periodValue={periodValue}
                            onChange={e => onChangeDates(e)}
                            options={[]}
                            onHideChange={() => setShowRangeDates(!showRangeDates)}
                        />
                    </div>

                    <div className='buttons-apply-filter'>
                        <Button
                            onClick={() => getReport()}
                            disabled={loading}
                            title={t('applyFilter')}
                        >
                            {t('applyFilter')}
                        </Button>
                    </div>
                </div>
            </div>
            { reportData && (
                <>                          
                    <div className='dois col-lg-8'>
                        <div className='row title-export-buttons'>
                            <div className='col report-title'>
                                <img src={graph} alt='graph' /> {t('viewingData')}
                            </div>
                            <div className='export-xls'>
                                <button disabled={loading} onClick={() => downloadFile()}>
                                    <img src={documentExcel} alt='documentExcel' /> {t('exportXLS')}
                                </button>
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-lg-12 col-xl-12 row gap-4 justify-content-center'>
                                <UniWidgetCardGraph 
                                    subtitle='Total de finalizados:'
                                    title1='Dentro do prazo'
                                    title='Total de atendimentos finalizados'
                                    title2='Em atraso'
                                    value1={reportDataFinished?.totalNotIsLate}
                                    value2={reportDataFinished?.totalIsLate}
                                    value3={reportDataFinished?.totalTickets}
                                    percent1={reportDataFinished?.percentNotIsLate}
                                    percent2={reportDataFinished?.percentIsLate}
                                />

                                <UniWidgetCardGraph 
                                    subtitle='Total de finalizados:'
                                    title1='Em aberto'
                                    title='Total de atendimentos'
                                    title2='Finalizados'
                                    value1={allTicketOpenedCount}
                                    value2={reportDataFinished?.totalTickets}
                                    value3={reportData?.totalTickets}
                                    percent1={Math.round((allTicketOpenedCount * 100) / reportData?.totalTickets)}
                                    percent2={Math.round(((reportDataFinished?.totalTickets ?? 0) * 100) / reportData?.totalTickets)}
                                    color1='#065E39'
                                    color2='#1BC76D'
                                    caculatePercent

                                />
                            </div>
                        </div>
                        <div className='report-card col-lg-12 col-xl-12 avg-months mt-4'>
                            {
                                loading ? (
                                    <Skeleton
                                        height={336}
                                        highlightColor={'#f5f5f5'}
                                        baseColor={'#ebebeb'}
                                    />
                                ) : (
                                    <div className='tabs'>
                                        <div className='tab-header'>
                                            <div className={'tab' + (currentTab === 1 ? ' tab-active': '')} onClick={() => setCurrentTab(1)}>
                                                {t('callCenterReportPage.resolved')} - 
                                                <span className='tab-subtitle'>{t('callCenterReportPage.bySector')}</span>
                                            </div>
                                            <div className={'tab' + (currentTab === 2 ? ' tab-active': '')}  onClick={() => setCurrentTab(2)}>
                                                {t('callCenterReportPage.resolved')} - 
                                                <span className='tab-subtitle'>{t('callCenterReportPage.byTicketLevel')}</span>
                                            </div>
                                        </div>
                                        {currentTab === 1 && (
                                            <UniWidgetDobleColumnReport
                                                options={filteredSectorData ?? []}
                                                filterOptions={[{description: 'Dentro do prazo', color: '#1BC76D', value:0},{description: 'Em atraso', color: '#FF6636', value:0}]}
                                                columnReportHeight={185}
                                                columnReportWidth={48}
                                                maxValue={20}
                                            />
                                        )}
                                        {currentTab === 2 && (
                                            <UniWidgetDobleColumnReport
                                                options={filteredTypeData ?? []}
                                                filterOptions={[{description: 'Dentro do prazo', color: '#1BC76D', value:0},{description: 'Em atraso', color: '#FF6636', value:0}]}
                                                columnReportHeight={185}
                                                columnReportWidth={48}
                                                maxValue={20}
                                            />
                                        )}                                    
                                    </div>
                                )
                            }
                        </div>
                    </div>
                </>
            )}  
            <UniModal
                icon={faXmarkCircle}
                message=''
                iconColor='#EA2027'
                title={errorAccessAs}
                onHide={() => {setErrorAccessAs('')}}
                show={errorAccessAs !== ''}
                isFontAwsome
            />
        </div>
    );
}

export default CallCenterReport;